import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { ClockIcon, LocationIcon, FieldIcon } from "./common/Icons.js";
import { fetchData } from "../utils/api.js";
import { categorizeScheduleDate } from "../utils/dateHelpers.js";
import {
  formatDateLabel,
  formatTimeLabel,
  formatFieldLabel,
  isGameCanceled,
  buildAppleMapsUrl,
  getUserTeamOutcome,
} from "../utils/scheduleHelpers.js";
import GameAttendance from "./GameAttendance.js";
import { ScheduleTabs } from "./common/ScheduleTabs.js";
import "./UserSchedule.css";

const fetchEntity = async (type, id, mapObj) => {
  if (!id || id === "undefined" || id === "null" || mapObj[id]) return;
  try {
    const queryParam = type === "teams" ? "team_id" : "location_id";
    const d = await fetchData(`/${type}?${queryParam}=eq.${id}`);
    const data = d?.length ? d[0] : null;
    if (data) {
      mapObj[id] = data;
    }
  } catch (e) {
    console.error(`Failed to fetch ${type} ${id}:`, e);
  }
};

const mergeHydratedGame = (game, fullGameMap) => {
  const full = fullGameMap[game.game_id || game.id];
  if (!full) return game;
  return {
    ...full,
    ...game,
    location_id: game.location_id ?? full.location_id,
    location_name: game.location_name ?? full.location_name,
    field_name: game.field_name ?? full.field_name,
    field_id: game.field_id ?? full.field_id,
    original_start_time: game.original_start_time ?? full.original_start_time,
    original_location_id:
      game.original_location_id ?? full.original_location_id,
    original_field_id: game.original_field_id ?? full.original_field_id,
    original_location_name:
      game.original_location_name ?? full.original_location_name,
    original_field_name: game.original_field_name ?? full.original_field_name,
    home_score: game.home_score ?? full.home_score,
    away_score: game.away_score ?? full.away_score,
    status: game.status ?? full.status,
  };
};

const hydrateMissingGameDetails = async (scheduleGames) => {
  const needsHydration = scheduleGames.some(
    (g) => g.location_id === undefined || g.home_score === undefined,
  );
  const gameIds = [
    ...new Set(scheduleGames.map((g) => g.game_id || g.id).filter(Boolean)),
  ];
  if (!needsHydration || gameIds.length === 0) return scheduleGames;
  try {
    const fullGames = await fetchData(
      `/games?game_id=in.(${gameIds.join(",")})`,
    );
    if (!Array.isArray(fullGames) || fullGames.length === 0) {
      return scheduleGames;
    }
    const fullGameMap = {};
    for (const fg of fullGames) {
      const fgId = fg.game_id || fg.id;
      if (fgId) fullGameMap[fgId] = fg;
    }
    return scheduleGames.map((g) => mergeHydratedGame(g, fullGameMap));
  } catch (e) {
    console.error("Failed to hydrate schedule game details:", e);
    return scheduleGames;
  }
};

const loadEntitiesForGames = async (gamesList) => {
  const newTeamsMap = {};
  const newLocationsMap = {};
  const teamIds = new Set();
  const locationIds = new Set();
  for (const game of gamesList) {
    if (game.home_team_id) teamIds.add(game.home_team_id);
    if (game.away_team_id) teamIds.add(game.away_team_id);
    if (game.location_id) locationIds.add(game.location_id);
    if (game.original_location_id) locationIds.add(game.original_location_id);
  }
  const teamPromises = Array.from(teamIds).map((id) =>
    fetchEntity("teams", id, newTeamsMap),
  );
  const locationPromises = Array.from(locationIds).map((id) =>
    fetchEntity("locations", id, newLocationsMap),
  );
  await Promise.all([...teamPromises, ...locationPromises]);
  return { newTeamsMap, newLocationsMap };
};

export const sortScheduleGames = (gamesList, tab) => {
  const copy = [...gamesList];
  copy.sort((a, b) => {
    const tA = new Date(a.start_time || 0).getTime();
    const tB = new Date(b.start_time || 0).getTime();
    return tab === "Past" ? tB - tA : tA - tB;
  });
  return copy;
};

const GameMetadataRow = ({
  startTime,
  canceled,
  rescheduled,
  locationName,
  appleMapsUrl,
  fieldName,
  isOfficiating,
  originalStartTime,
  originalLocationName,
  originalFieldName,
}) => {
  const dateChanged = Boolean(
    originalStartTime && originalStartTime !== startTime,
  );
  const locationChanged = Boolean(
    originalLocationName && originalLocationName !== locationName,
  );
  const fieldChanged =
    locationChanged ||
    Boolean(originalFieldName && originalFieldName !== fieldName);

  return (
    <div className="wireframe-game-meta-row">
      <span className="wireframe-meta-item">
        <ClockIcon size="xs" />
        <span className="wireframe-meta-datetime-col">
          <span className="wireframe-meta-datetime">
            {formatDateLabel(startTime)} {formatTimeLabel(startTime)}
          </span>
          {dateChanged && (
            <span className="wireframe-meta-original">
              {formatDateLabel(originalStartTime)}{" "}
              {formatTimeLabel(originalStartTime)}
            </span>
          )}
        </span>
        {canceled && <span className="game-canceled-badge">Canceled</span>}
        {rescheduled && (
          <span className="game-rescheduled-badge">Rescheduled</span>
        )}
      </span>

      <span className="wireframe-meta-item">
        <LocationIcon size="xs" />
        <span className="wireframe-meta-location-col">
          {appleMapsUrl ? (
            <a
              href={appleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="wireframe-venue-link"
            >
              {locationName}
            </a>
          ) : (
            <span>{locationName}</span>
          )}
          {locationChanged && (
            <span className="wireframe-meta-original">
              {originalLocationName}
            </span>
          )}
        </span>
      </span>

      {(fieldName || (fieldChanged && originalFieldName)) && (
        <span className="wireframe-meta-item">
          <FieldIcon size="xs" />
          <span className="wireframe-meta-field-col">
            {fieldName && <span>{fieldName}</span>}
            {fieldChanged && originalFieldName && (
              <span className="wireframe-meta-original">
                {originalFieldName}
              </span>
            )}
          </span>
        </span>
      )}

      {isOfficiating && (
        <span className="wireframe-officiating-label">Officiating</span>
      )}
    </div>
  );
};

GameMetadataRow.propTypes = {
  startTime: PropTypes.string,
  canceled: PropTypes.bool,
  rescheduled: PropTypes.bool,
  locationName: PropTypes.string,
  appleMapsUrl: PropTypes.string,
  fieldName: PropTypes.string,
  isOfficiating: PropTypes.bool,
  originalStartTime: PropTypes.string,
  originalLocationName: PropTypes.string,
  originalFieldName: PropTypes.string,
};

const UserSchedule = ({ user }) => {
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [scheduleTab, setScheduleTab] = useState("Upcoming");
  const [teamsMap, setTeamsMap] = useState({});
  const [locationsMap, setLocationsMap] = useState({});
  const [playerTeams, setPlayerTeams] = useState([]);

  const fetchSchedule = useCallback(async () => {
    const firebaseId = user?.firebaseUid || user?.id;
    if (!firebaseId) return;
    try {
      setIsLoading(true);

      const rawData = await fetchData(
        `/user_schedule?user_firebase_id=eq.${firebaseId}`,
      );
      if (!rawData) return;

      const data = await hydrateMissingGameDetails(rawData);
      setGames(data);

      const profileData = await fetchData(
        `/user_teams?firebase_id=eq.${firebaseId}`,
      );
      if (profileData) {
        setPlayerTeams(profileData.map((pt) => pt.team_id));
      }

      const { newTeamsMap, newLocationsMap } = await loadEntitiesForGames(data);
      setTeamsMap(newTeamsMap);
      setLocationsMap(newLocationsMap);
    } catch (e) {
      console.error("Failed to fetch schedule", e);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const sortedFilteredGames = useMemo(() => {
    const matching = games.filter(
      (g) => categorizeScheduleDate(g.start_time) === scheduleTab,
    );
    return sortScheduleGames(matching, scheduleTab);
  }, [games, scheduleTab]);

  if (!user) {
    return (
      <div className="empty-state">
        <p>Please log in to view your schedule.</p>
      </div>
    );
  }

  if (isLoading) {
    return <div className="loading-spinner" />;
  }

  return (
    <div className="user-schedule dashboard-panel">
      <ScheduleTabs activeTab={scheduleTab} onTabChange={setScheduleTab} />

      <div className="games-list">
        {sortedFilteredGames.length === 0 ? (
          <div className="empty-state schedule-empty-state">
            <p>No {scheduleTab.toLowerCase()} games found.</p>
          </div>
        ) : (
          sortedFilteredGames.map((game, idx) => {
            const gameId = game.game_id || game.id;
            const rowKey = gameId ? `game-${gameId}-${idx}` : `game-${idx}`;
            const canceled = isGameCanceled(game);
            const isPastTab = scheduleTab === "Past";
            const hasScores =
              game.home_score !== null &&
              game.home_score !== undefined &&
              game.away_score !== null &&
              game.away_score !== undefined;

            const homeTeamName =
              teamsMap[game.home_team_id]?.name ||
              teamsMap[game.home_team_id]?.team_name ||
              "Home Team";
            const awayTeamName =
              teamsMap[game.away_team_id]?.name ||
              teamsMap[game.away_team_id]?.team_name ||
              "Away Team";
            const locationObj = locationsMap[game.location_id];
            const locationName =
              game.location_name || locationObj?.name || "Field Location";
            const locationAddress =
              game.location_address || locationObj?.address || "";
            const appleMapsUrl = buildAppleMapsUrl(
              locationName,
              locationAddress,
            );
            const fieldName = formatFieldLabel(
              game.field_name || game.field_id,
            );
            const origLocObj = locationsMap[game.original_location_id];
            const originalLocationName =
              game.original_location_name || origLocObj?.name || "";
            const origFieldRaw =
              game.original_field_name || game.original_field_id;
            const originalFieldName = origFieldRaw
              ? formatFieldLabel(origFieldRaw)
              : "";
            const userOutcome = getUserTeamOutcome(game, playerTeams);

            return (
              <div
                key={rowKey}
                className={`game-card wireframe-schedule-row ${canceled ? "game-canceled" : ""}`}
              >
                <GameMetadataRow
                  startTime={game.start_time}
                  canceled={canceled}
                  rescheduled={game.status === "rescheduled"}
                  locationName={locationName}
                  appleMapsUrl={appleMapsUrl}
                  fieldName={fieldName}
                  isOfficiating={Boolean(game.umpire_ids?.includes(user?.id))}
                  originalStartTime={game.original_start_time}
                  originalLocationName={originalLocationName}
                  originalFieldName={originalFieldName}
                />

                <div className="wireframe-game-main-row">
                  <div className="wireframe-game-left">
                    {isPastTab && (
                      <span
                        className={`past-outcome-letter ${userOutcome?.statusClass || ""}`.trim()}
                        data-testid={
                          userOutcome ? "user-team-outcome" : undefined
                        }
                      >
                        {userOutcome ? userOutcome.letter : ""}
                      </span>
                    )}
                    <div className="wireframe-teams-stack">
                      <span className="wireframe-team-name">
                        {awayTeamName}
                      </span>
                      <span className="wireframe-team-name">
                        {homeTeamName}
                      </span>
                    </div>
                  </div>

                  <div className="wireframe-game-right">
                    {(isPastTab || hasScores) && (
                      <div className="wireframe-scores-stack">
                        <span className="past-condensed-score">
                          {game.away_score ?? "-"}
                        </span>
                        <span className="past-condensed-score">
                          {game.home_score ?? "-"}
                        </span>
                      </div>
                    )}

                    {!isPastTab && !canceled && (
                      <GameAttendance
                        gameId={gameId}
                        user={user}
                        isPlayer={
                          playerTeams.includes(game.home_team_id) ||
                          playerTeams.includes(game.away_team_id)
                        }
                        isCoach={
                          teamsMap[game.home_team_id]?.coach_id === user.id ||
                          teamsMap[game.away_team_id]?.coach_id === user.id
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

UserSchedule.propTypes = {
  user: PropTypes.object,
};

export default UserSchedule;
